import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { TwicVideo } from '@twicpics/components/react';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';

import styles from './ProductGalleryVideo.module.scss';

type ProductGalleryVideoProps = {
  srcPath: string;
  title: string;
};

const ProductGalleryVideo = ({
  srcPath,
  title,
}: ProductGalleryVideoProps): JSX.Element => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoWrapperRef = useRef(null);
  const videoStateRef = useRef(null);
  const videoElemRef = useRef<HTMLVideoElement | null>(null);
  const [videoIsPaused, setVideoIsPaused] = useState(false);
  const animate = (
    element: HTMLElement,
    animationClass: string,
    onEnd: (element: HTMLElement) => void
  ) => {
    element.classList.add(animationClass);
    element.addEventListener(
      'animationend',
      function () {
        element.classList.remove(animationClass);
        onEnd(element);
      },
      { once: true }
    );
  };

  /**
   * Play / Pause video
   */
  const togglePlayPause = (videoElement, videoState, testFullscreen = true) => {
    if (
      testFullscreen &&
      !videoElement.closest('.image-gallery-content.fullscreen')
    ) {
      return;
    }

    const isPaused = videoElement.paused;
    isPaused ? videoElement.play() : videoElement.pause();

    videoState.classList.add(styles.active);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      animate(videoState, styles['slow-hide'], element =>
        element.classList.remove(styles.active)
      );
    }, 1000);
  };

  /**
   * Update videoIsPaused state value
   */
  const switchVideoIsPaused = (isPaused: boolean) => {
    setVideoIsPaused(isPaused);
  };

  /**
   * Play pause button trigger
   */
  const playPauseButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    togglePlayPause(videoElemRef.current, videoStateRef.current, false);
  };

  useEffect(() => {
    const videoWrapper = videoWrapperRef.current as HTMLElement | null;
    const videoState = videoStateRef.current as HTMLElement | null;
    const videoElement = (videoElemRef.current = videoWrapper
      ? videoWrapper.querySelector('video')
      : null);

    if (videoElement && videoState) {
      videoElement.addEventListener(
        'play',
        switchVideoIsPaused.bind(null, false)
      );

      videoElement.addEventListener(
        'pause',
        switchVideoIsPaused.bind(null, true)
      );

      videoElement.addEventListener(
        'click',
        togglePlayPause.bind(null, videoElement, videoState, true)
      );
    }

    return () => {
      videoElement?.removeEventListener(
        'play',
        switchVideoIsPaused.bind(null, false)
      );

      videoElement?.removeEventListener(
        'pause',
        switchVideoIsPaused.bind(null, true)
      );

      videoElement?.removeEventListener(
        'click',
        togglePlayPause.bind(null, videoElement, videoState, true)
      );

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={videoWrapperRef}
      className={classnames(styles.wrapper, 'wrapper-video')}
    >
      <div className={styles.control}>
        <div className={styles.controlMiddle}>
          <div ref={videoStateRef} className={styles.videoState}>
            {videoIsPaused ? <PauseIcon /> : <PlayArrowIcon />}
          </div>
          <button className={styles.videoPauseButton} onClick={playPauseButton}>
            {videoIsPaused ? <PlayArrowIcon /> : <PauseIcon />}
          </button>
        </div>
      </div>
      <TwicVideo ratio="3/4" src={srcPath} title={title} />
    </div>
  );
};

export default ProductGalleryVideo;
